import { ParsedUrlQuery } from 'querystring';

import { GetStaticPaths, GetStaticProps } from 'next';

import { Layout } from '@/components/Layout';
import { PhoneFooter } from '@/components/sections/PhoneFooter';
import { PlanYourVisit } from '@/components/sections/PlanYourVisit';
import { ContentWithSidebar } from '@/components/shared/ContentWithSidebar';
import { EmptyContent } from '@/components/shared/EmptyContent';
import { PageHeading } from '@/components/shared/PageHeading';
import api from '@/helpers/api';
import { sortSiteMenu } from '@/helpers/common';
import getPageBanner from '@/helpers/getPageBanner';
import { getI18nPaths, getI18nProps } from '@/helpers/i18n';
import { usePageSeo } from '@/hooks/usePageSeo';
import { COMMON_LOCALE_PART } from '@/locale';
import { IPage, IPageDetailsProps, Locale, SiteMenuItem } from '@/types';
import { ITicketSectionInfo } from '@/types/types/Pages';

interface IRulesPageProps extends IPageDetailsProps {
  tickets: ITicketSectionInfo[];
}

export default function RulesPage({ siteMenu, pageDetails, tickets }: IRulesPageProps) {
  const entryTickets = tickets.find((ticketsData) => ticketsData.slug === 'tickets') ?? null;
  const additionalServices = tickets.find((ticketsData) => ticketsData.slug === 'additional-services') ?? null;
  const excursions = tickets.find((ticketsData) => ticketsData.slug === 'tours') ?? null;

  const { seoOptions } = usePageSeo(pageDetails);

  if (!pageDetails || !tickets) {
    return <EmptyContent />;
  }

  return (
    <>
      <Layout siteMenu={siteMenu} seoOptions={seoOptions} banner={getPageBanner(pageDetails)}>
        <ContentWithSidebar siteMenu={siteMenu}>
          <PageHeading>{pageDetails.name}</PageHeading>

          <PlanYourVisit entryTickets={entryTickets} additionalServices={additionalServices} excursions={excursions} />
        </ContentWithSidebar>
        <PhoneFooter />
      </Layout>
    </>
  );
}

interface IParams extends ParsedUrlQuery {
  locale: Locale;
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    fallback: false,
    paths: getI18nPaths(),
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { locale } = params as IParams;

  const request = api({ locale: locale });

  const responses = await Promise.allSettled([
    request<SiteMenuItem[]>('site-menu/'),
    request<IPage>('pages/detail/plan-your-visit'),
    request<ITicketSectionInfo[]>('tickets'),
  ]);

  const siteMenu = responses[0].status === 'fulfilled' ? responses[0].value.data : undefined;
  const pageDetails = responses[1].status === 'fulfilled' ? responses[1].value.data : undefined;
  const tickets = responses[2].status === 'fulfilled' ? responses[2].value.data : undefined;

  return {
    props: {
      ...(await getI18nProps(locale, [COMMON_LOCALE_PART])),
      siteMenu: sortSiteMenu(locale, siteMenu as SiteMenuItem[]),
      pageDetails,
      tickets,
    },
  };
};
